import React, { useEffect, useState } from "react";
import cx from "classnames";
import { useParams } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { SelectPagination, ThemeStore } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import { Group, Link, LinkPage } from "@models/ISchema";
import { IParams } from "@models/IParams";
import { IOption } from "@common/components/Select/Select";
import { LINKS_QUERY } from "@common/layout/components/context-search/graphql/contextsearch.queries";
import i18n from "@config/i18n";
import css from "./linkSelector.module.scss";

interface LinkOption {
  label: string;
  value: string;
}

type SelectedExternalOptions = { [key: string]: Link };

const getRegularLabel = (link: Link, isSelected: boolean) => {
  return (
    <div className={cx(css.linkLabelWrapper, isSelected && css.linkLabelWrapper__selected)}>
      <div className={css.linkOptionLabel}>
        <p className={css.linkOptionLabel_mainLabel}>
          {i18n.t("selectors.link-selector.link-code")}: <b>{link?.code}</b>
        </p>

        <p className={css.linkOptionLabel_secondaryLabel}>
          {i18n.t("selectors.link-selector.course")}: <b>{link?.course?.name}</b> |{" "}
          {i18n.t("selectors.link-selector.campus")}:{" "}
          <b>{link?.course?.curriculum?.program?.campus?.code}</b> |{" "}
          {i18n.t("selectors.link-selector.term")}: <b>{link?.bundle?.term?.name}</b>
        </p>
      </div>

      {isSelected && <Icon className={css.linkLabelWrapper_icon} name="check" size="md" />}
    </div>
  );
};

const getLinkGroupLabel = (link: Link, group: Group | null, isSelected: boolean) => {
  return (
    <div className={cx(css.linkLabelWrapper, isSelected && css.linkLabelWrapper__selected)}>
      <div className={cx(css.linkOptionLabel, css.linkOptionLabel__groups)}>
        <p className={css.linkOptionLabel_mainLabel}>
          {i18n.t("selectors.link-selector.group-link")}: <b>{link?.code}</b>{" "}
          {group && (
            <>
              <b>|</b> {i18n.t("selectors.link-selector.group")}: <b>{group?.code}</b>
            </>
          )}
        </p>

        <p className={css.linkOptionLabel_secondaryLabel}>
          {i18n.t("selectors.link-selector.term")}: <b>{link?.bundle?.term?.name}</b> <b>|</b>{" "}
          {i18n.t("selectors.link-selector.campus")}:{" "}
          <b>{link?.course?.curriculum?.program?.campus?.code}</b>
        </p>

        <p className={css.linkOptionLabel_secondaryLabel}>
          {i18n.t("selectors.link-selector.course")}: <b>{link?.course?.name}</b>
        </p>
      </div>

      {isSelected && <Icon className={css.linkLabelWrapper_icon} name="check" size="md" />}
    </div>
  );
};

const getOriginalTermOption = (
  link: Link,
  group: Group | null,
  useGroupLabel: boolean,
  selectedExternalOptions: SelectedExternalOptions,
): LinkOption => {
  if (!link?.code || !link?.id) {
    return {
      label: "",
      value: "",
    };
  }

  const isSelected = !!selectedExternalOptions?.[link?.id];

  return {
    label: useGroupLabel
      ? getLinkGroupLabel(link, group, isSelected)
      : (getRegularLabel(link, isSelected) as any),
    value: link?.id,
  };
};

const getLinkOption = (
  link: Link | LinkOption,
  group: Group | null,
  useGroupLabel: boolean,
  selectedExternalOptions: SelectedExternalOptions,
): LinkOption => {
  const linkOption = link as LinkOption;

  if (linkOption?.value && linkOption?.label) return linkOption;

  return getOriginalTermOption(link as Link, group, useGroupLabel, selectedExternalOptions);
};

interface LinkSelectorProps {
  className?: string;
  isClearable?: boolean;
  label?: string;
  placeholder?: string;
  keepSelection?: boolean;
  isDisabled?: boolean;
  linksToAvoid?: string[];
  selectedExternalOptions?: SelectedExternalOptions;
  splitByGroups?: boolean;
  campusId?: string;
  getUnrestrictedLinks?: boolean;
  onlyCoursesGeneratingPackages?: boolean;
  sendValues?: (payload: { link: string | null }) => void;
  onSelectLink?: (link?: Link) => void;
}

const savedOptions: { [key: string]: Link } = {};

const LinkSelector: React.FC<LinkSelectorProps> = props => {
  const {
    className = "",
    isClearable = true,
    label = "Liga",
    placeholder = "Seleccionar Liga",
    sendValues,
    onSelectLink,
    isDisabled = false,
    linksToAvoid = [],
    keepSelection = true,
    selectedExternalOptions = {},
    splitByGroups = false,
    campusId,
    getUnrestrictedLinks,
    onlyCoursesGeneratingPackages,
  } = props;
  const client = useApolloClient();
  const { scenario, origin }: IParams = useParams();
  const [valueLink, setValueLink] = useState<LinkOption | null>(null);
  const [prevSearchTerm, setPrevSearchTerm] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [isRefetching, setIsRefetching] = useState(false);
  const theme = ThemeStore.useThemeStore(state => state?.theme);

  useEffect(() => {
    setIsRefetching(true);
  }, [selectedExternalOptions]);

  const queryOptions = (searchTerm = "", page = 1) => {
    const pageSize = 20;

    const useFields =
      !!campusId || getUnrestrictedLinks !== undefined || onlyCoursesGeneratingPackages === true;
    const fields = {
      ...(campusId
        ? {
            campusId: {
              eq: campusId,
            },
          }
        : {}),
      ...(getUnrestrictedLinks !== undefined
        ? {
            getUnrestrictedLinks: {
              is: getUnrestrictedLinks,
            },
          }
        : {}),
      ...(onlyCoursesGeneratingPackages === true
        ? {
            course: {
              generatesPackages: {
                is: true,
              },
            },
          }
        : {}),
    };

    return {
      query: LINKS_QUERY,
      variables: {
        scenarioId: scenario,
        originId: origin,
        filter: {
          fields: useFields ? fields : undefined,
          pagination: {
            page,
            size: pageSize,
            searchTerm,
          },
        },
      },
    };
  };

  const getTermOptions = async (searchTerm = "", page = 1) => {
    try {
      const query = queryOptions(searchTerm, page);
      const { data } = await client.query(query);

      const linksPage = data?.cube?.links as LinkPage;
      const links = linksPage?.items;
      const options = [];
      const filteredOptions = (links ?? []).filter(link => !linksToAvoid?.includes(link?.id));

      filteredOptions.forEach(link => {
        if (!(link.id in savedOptions)) {
          savedOptions[link.id] = link;
        }

        if (splitByGroups) {
          link?.groups?.length
            ? link?.groups?.forEach(group => {
                if (group?.campus?.id === campusId) {
                  options.push(getLinkOption(link, group, true, selectedExternalOptions));
                }
              })
            : options.push(getLinkOption(link, null, true, selectedExternalOptions));
        } else {
          options.push(getLinkOption(link, null, false, selectedExternalOptions));
        }
      });

      return {
        options: [...options],
        pageInfo: linksPage?.pageInfo,
      };
    } catch (error) {
      console.log(error);
    }
  };

  const loadLinks = async () => {
    const hasSameTerm = prevSearchTerm === searchTerm;
    const newSearchPage = hasSameTerm && !isRefetching ? page + 1 : 1;

    setIsRefetching(false);
    setPrevSearchTerm(searchTerm);
    setPage(newSearchPage);

    const { pageInfo, options } = await getTermOptions(searchTerm, newSearchPage);

    return {
      options,
      hasMore: pageInfo?.hasNextPage,
      additional: { page },
    };
  };

  const handleOnChange = (linkOption: IOption) => {
    setValueLink(linkOption || null);
    sendValues?.({
      link: linkOption?.value || null,
    });

    onSelectLink?.(savedOptions?.[linkOption?.value]);
  };

  return (
    <SelectPagination
      key={`link-selector-${Object.keys(selectedExternalOptions)?.length || 0}`}
      className={cx(className, css.linkSelector, theme.mode === "dark" && css.linkSelector__dark)}
      onInputChange={setSearchTerm}
      label={label}
      value={keepSelection ? valueLink : null}
      debounceTimeout={500}
      placeholder={placeholder}
      isClearable={isClearable}
      onChange={handleOnChange}
      loadOptions={loadLinks}
      isDisabled={isDisabled}
      // Set to true to leave the filter options to backend and service handlers
      filterOption={() => true}
    />
  );
};

export default LinkSelector;
